import { FC, memo } from 'react'
import { PropertyTypeSectionTransport } from '../../../transports/home-page-transport'
import { useTranslation, useLocale } from '../../../hooks/locale'
import {
    Container,
    TitleContainer,
    ListContainer,
    SliderContainer,
    Cell,
    Item,
    ImageContainer,
    PropertyTypeImage,
    Label,
} from './styles'
import { useResponsiveValue } from '../../../hooks/responsive'
import { Title, Subtitle } from '../common/styles'
import { getBasePath } from '../../../utils/miscellaneous-utils'
import { THUMBNAIL_MD_IMAGE_WIDTH } from '../../../common/image-widths'
import { EmblaCarousel } from '../../common/embla-carousel'

type Props = {
    section: PropertyTypeSectionTransport
    totalOnlineListingsCount?: number | null
}

const breakpoints = [
    { query: '(max-width: 399px)', value: 4.2 },
    { query: '(min-width: 400px) and (max-width: 450px)', value: 4.8 },
    { query: '(min-width: 451px) and (max-width: 479px)', value: 5.2 },
    { query: '(min-width: 480px) and (max-width: 519px)', value: 5.5 },
    { query: '(min-width: 520px) and (max-width: 639px)', value: 6.5 },
    { query: '(min-width: 640px) and (max-width: 767px)', value: 8.5 },
    { query: '(min-width: 768px) and (max-width: 900px)', value: 5.5 },
    { query: '(min-width: 901px) and (max-width: 1023px)', value: 6.5 },
    { query: '(min-width: 1024px)', value: 7.5 },
]

const _getPropertyTypeImage = (id: number) => {
    const basePath = `/assets/images/property-types/${id}.jpg?imwidth=${THUMBNAIL_MD_IMAGE_WIDTH}`
    return getBasePath(basePath)
}

const FALLBACK_COUNT = '133.921'

const PropertyTypesSection: FC<Props> = ({ section, totalOnlineListingsCount }) => {
    const { items } = section || { items: [] }
    const { t } = useTranslation()
    const { locale } = useLocale()
    const visibleSlides = useResponsiveValue(breakpoints, 2.5)
    const slidesToScroll = Math.floor(visibleSlides)

    const formattedCount = totalOnlineListingsCount?.toLocaleString(locale) || FALLBACK_COUNT

    if (!items?.length) {
        return null
    }

    return (
        <Container>
            <TitleContainer>
                <Title>{t('pages.home.propertyTypes.heading')}</Title>
                <Subtitle>{t('pages.home.propertyTypes.subheading', { count: formattedCount })}</Subtitle>
            </TitleContainer>
            <ListContainer>
                <SliderContainer>
                    <EmblaCarousel
                        slides={items.map(item => (
                            <Cell href={item.link || '#'} key={item.id}>
                                <Item>
                                    <ImageContainer>
                                        <PropertyTypeImage
                                            src={_getPropertyTypeImage(item.id)}
                                            loading="lazy"
                                            alt={item.label}
                                        />
                                    </ImageContainer>
                                    <Label>{item.label}</Label>
                                </Item>
                            </Cell>
                        ))}
                        visibleSlides={visibleSlides}
                        slidesToScroll={slidesToScroll}
                        showNav
                        navVariant="top-right"
                        navPosition={{
                            desktopTop: '-5rem',
                            desktopRight: '1rem',
                            wideTop: '-5rem',
                            wideRight: '1rem',
                        }}
                        viewportPadding="0.1rem"
                    />
                </SliderContainer>
            </ListContainer>
        </Container>
    )
}

export default memo(PropertyTypesSection)
